import axios from 'axios';
import config from '../config.js';

/**
 * Authentication API service
 * Handles all authentication-related API calls
 */

// Create axios instance for auth API
const authApi = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.defaultTimeout,
  headers: {
    'Content-Type': 'application/json',
    ...config.api.headers
  }
});

// Request interceptor to add auth token
authApi.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
authApi.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 (Unauthorized) and we haven't tried refreshing yet
    // BUT: Don't try to refresh token for login/register endpoints (they're public)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                           originalRequest.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      
      console.log('[DEBUG] authApi interceptor: Attempting token refresh for 401', {
        url: originalRequest.url,
        isAuthEndpoint
      });
      
      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.log('[DEBUG] authApi interceptor: No refresh token available');
          throw new Error('No refresh token available');
        }
        
        const response = await axios.post(
          `${config.api.baseURL}/auth/refresh`,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Update tokens in localStorage
        const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;
        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }
        
        // Update the Authorization header
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        
        // Retry the original request
        return authApi(originalRequest);
      } catch (refreshError) {
        console.log('[DEBUG] authApi interceptor: Token refresh failed', {
          error: refreshError.message,
          url: originalRequest.url
        });
        
        // Refresh failed, clear auth data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        // DON'T redirect on login/register errors - let the component handle it
        // Only redirect for other endpoints that require auth
        if (!isAuthEndpoint && !window.location.pathname.includes('/login')) {
          console.log('[DEBUG] authApi interceptor: Redirecting to home (non-auth endpoint)');
          window.location.href = '/';
        } else {
          console.log('[DEBUG] authApi interceptor: NOT redirecting (auth endpoint or already on login page)');
        }
        
        // Create a structured error for session expiration
        const sessionError = new Error('Session expired. Please login again.');
        sessionError.response = {
          status: 401,
          data: {
            success: false,
            error: 'Session expired',
            message: 'Session expired. Please login again.',
            code: 'SESSION_EXPIRED'
          }
        };
        return Promise.reject(sessionError);
      }
    }
    
    // For login/register errors, log and reject without redirect
    if (isAuthEndpoint && error.response?.status === 401) {
      console.log('[DEBUG] authApi interceptor: Login/Register 401 error - NOT redirecting', {
        url: originalRequest.url,
        responseData: error.response?.data
      });
    }
    
    // For other errors, preserve the full error object
    // The errorHandler utility needs the full error response
    return Promise.reject(error);
  }
);

const authApiService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration response
   */
  async register(userData) {
    return authApi.post('/auth/register', userData);
  },

  /**
   * Login user
   * @param {string} identifier - Email or username
   * @param {string} password - Password
   * @returns {Promise<Object>} Login response
   */
  async login(identifier, password) {
    console.log('[DEBUG] authApiService.login: Called', { identifier, hasPassword: !!password });
    try {
      const response = await authApi.post('/auth/login', { identifier, password });
      console.log('[DEBUG] authApiService.login: Success response', {
        hasTokens: !!response.tokens,
        hasUser: !!response.user,
        responseKeys: Object.keys(response)
      });
      return response;
    } catch (error) {
      console.log('[DEBUG] authApiService.login: Error caught', {
        errorType: typeof error,
        isAxiosError: error.isAxiosError,
        hasResponse: !!error.response,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
        errorMessage: error.message,
        errorKeys: Object.keys(error)
      });
      throw error;
    }
  },

  /**
   * Refresh access token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} Token refresh response
   */
  async refreshToken(refreshToken) {
    return authApi.post('/auth/refresh', { refreshToken });
  },

  /**
   * Logout user
   * @param {string} token - Access token to invalidate
   * @returns {Promise<Object>} Logout response
   */
  async logout(token) {
    return authApi.post('/auth/logout', {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  },

  /**
   * Verify email
   * @param {string} token - Verification token
   * @returns {Promise<Object>} Verification response
   */
  async verifyEmail(token) {
    return authApi.get(`/auth/verify-email/${token}`);
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} Password reset request response
   */
  async requestPasswordReset(email) {
    return authApi.post('/auth/request-password-reset', { email });
  },

  /**
   * Reset password
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password reset response
   */
  async resetPassword(token, newPassword) {
    return authApi.post('/auth/reset-password', { token, newPassword });
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile
   */
  async getCurrentUser() {
    return authApi.get('/auth/me');
  },

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} Updated user profile
   */
  async updateProfile(profileData) {
    return authApi.put('/auth/me', profileData);
  },

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password change response
   */
  async changePassword(currentPassword, newPassword) {
    return authApi.post('/auth/change-password', { currentPassword, newPassword });
  },

  /**
   * Get Google OAuth authorization URL
   * @returns {Promise<Object>} OAuth URL and state
   */
  async getGoogleOAuthUrl() {
    return authApi.get('/auth/google/url');
  },

  /**
   * Social login - Google
   * @param {string} code - Authorization code from Google
   * @param {string} state - OAuth state token (optional)
   * @returns {Promise<Object>} Login response
   */
  async googleLogin(code, state = null) {
    return authApi.post('/auth/google', { code, state });
  },

  /**
   * Social login - GitHub
   * @param {string} code - Authorization code from GitHub
   * @returns {Promise<Object>} Login response
   */
  async githubLogin(code) {
    return authApi.post('/auth/github', { code });
  },

  /**
   * Social login - LinkedIn
   * @param {string} code - Authorization code from LinkedIn
   * @returns {Promise<Object>} Login response
   */
  async linkedinLogin(code) {
    return authApi.post('/auth/linkedin', { code });
  },

  /**
   * WeChat QR code login - Generate QR code
   * @returns {Promise<Object>} QR code data
   */
  async generateWeChatQRCode() {
    return authApi.get('/auth/wechat/qrcode');
  },

  /**
   * WeChat QR code login - Check status
   * @param {string} qrCodeId - QR code ID
   * @returns {Promise<Object>} QR code status
   */
  async checkWeChatQRCodeStatus(qrCodeId) {
    return authApi.get(`/auth/wechat/qrcode/${qrCodeId}`);
  },

  /**
   * Health check for auth service
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    return authApi.get('/auth/health');
  }
};

export default authApiService;